import { useTheme } from '@mui/material';
import { type ChangeEvent, type MouseEvent, type ReactNode, useMemo, useState } from 'react';

import { BusinessIcon } from '../../../icons';
import type { ResourceMenuItem } from '../../organisms/ResourceMenuActions';

export type TaskItemAction = ResourceMenuItem;

export type TaskItemData = {
  id?: string | number;
  title: string;
  state?: 'pending' | 'completed' | string;
  completed?: boolean;
  subTopicName?: string | null;
  sub_topics?: { id?: string | number; name: string } | null;
  providerName?: string | null;
  provider_name?: string | null;
};

export type TaskItemProps = {
  task?: TaskItemData;
  title?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean, event?: ChangeEvent<HTMLInputElement>) => void;
  onToggle?: (checked: boolean) => void;
  subTopicName?: string;
  subTopicHref?: string;
  onSubTopicClick?: (event?: MouseEvent<HTMLAnchorElement | HTMLElement>) => void;
  secondary?: ReactNode;
  renderSecondary?: () => ReactNode;
  providerName?: string | null;
  action?: ReactNode;
  actions?: (TaskItemAction | null | undefined | false)[];
  actionsButtonTooltip?: string;
  actionsButtonAriaLabel?: string;
  disabled?: boolean;
  'data-testid'?: string;
};

export type OpenTaskItemProps = TaskItemProps;

export function useTaskItem(props: TaskItemProps) {
  const isControlled = props.checked !== undefined;
  const initialChecked =
    props.checked ??
    props.defaultChecked ??
    props.task?.completed ??
    (props.task?.state !== undefined ? props.task.state === 'completed' : false);

  const [internalChecked, setInternalChecked] = useState<boolean>(Boolean(initialChecked));

  const isChecked = isControlled ? Boolean(props.checked) : internalChecked;

  function handleToggle(event: ChangeEvent<HTMLInputElement>) {
    if (props.disabled) {
      return;
    }
    const nextChecked = event.target.checked;
    if (!isControlled) {
      setInternalChecked(nextChecked);
    }
    props.onChange?.(nextChecked, event);
    props.onToggle?.(nextChecked);
  }

  const title = props.title ?? props.task?.title ?? '';
  const subTopicName =
    props.subTopicName ??
    props.task?.subTopicName ??
    props.task?.sub_topics?.name ??
    null;

  const providerName =
    props.providerName ??
    props.task?.providerName ??
    props.task?.provider_name ??
    null;

  const theme = useTheme();
  const ProviderIcon = theme.resourceIcons?.providers ?? BusinessIcon;

  const actions = useMemo(() => {
    return (props.actions ?? []).filter(Boolean) as TaskItemAction[];
  }, [props.actions]);

  const hasActions = actions.length > 0;

  return {
    isChecked,
    handleToggle,
    title,
    subTopicName,
    providerName,
    ProviderIcon,
    actions,
    hasActions,
    actionsButtonTooltip: props.actionsButtonTooltip,
    actionsButtonAriaLabel: props.actionsButtonAriaLabel,
  };
}

export const useOpenTaskItem = useTaskItem;
